import { useCallback, useEffect, useState } from "react";
import {
  PaymentMethodVM,
  PaymentMethodEntityTypesEnum,
} from "@propertelligent/client-api";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import { PlaidAccountDetails } from "../../../components/firstTimeUserExperience/Context";
import PaymentAccountCardItem from "../../../newDashboard/Pages/payment-accounts/PaymentAccountCardItem";
import PlaidPaymentAccountModals from "../../../newDashboard/Pages/payment-accounts/PlaidPaymentAccountModals";
import EditPlaidAccountNameModal from "../../../newDashboard/Pages/payment-accounts/EditPlaidAccountNameModal";
import { fetchPlatformPaymentMethodList } from "./platformPaymentAccountsApi";
import "../../../newDashboard/Pages/payment-accounts/PaymentAccountCardItem.css";

// The platform collects every organization's subscription fee through a single receiving Plaid gateway,
// so only one platform payment account is meaningful (backend also caps this at 1).
const PLATFORM_PAYMENT_METHOD_LIMIT = 1;

// PaymentMethodEntityTypesEnum.Platform (= 4). Sent so the PaymentMethodVM "EntityTypeId != 0" guard passes
// (the old All=0 tripped "Entity Type cannot be 0"); the backend re-derives the platform entity from the role.
// TODO: swap to PaymentMethodEntityTypesEnum.Platform once the generated @propertelligent/client-api is regenerated.
const PLATFORM_PM_ENTITY_TYPE_ID = 4 as PaymentMethodEntityTypesEnum;

/**
 * Platform super-admin "Payment Accounts" — mirrors the landlord portal's Payment Accounts screen but for the
 * platform's own Plaid receiving gateway (used to collect subscription fees from organizations). Gated purely by
 * the App.tsx Platform role branch (a Platform JWT carries no per-module permission map), so no PermissionGuard.
 *
 * The list is fetched through the raw-fetch bridge in ./platformPaymentAccountsApi (fetchPlatformPaymentMethodList)
 * to the new /api/PaymentMethod/plaid-payment-method/platform-list endpoint because the typed client method
 * (PaymentService.getApiPaymentMethodPlatformList) does not exist until CI regenerates @propertelligent/client-api.
 * Add/Reconnect/Delete already reuse the shared Plaid components, which call the existing typed client methods.
 */
const PlatformPaymentAccountsPage = () => {
  const [rows, setRows] = useState<PaymentMethodVM[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasAnyPaymentAccounts, setHasAnyPaymentAccounts] = useState<boolean>(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isReconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [reconnectPaymentMethod, setReconnectPaymentMethod] = useState<PaymentMethodVM | null>(null);
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [renamePaymentMethod, setRenamePaymentMethod] = useState<PaymentMethodVM | null>(null);

  const fetchPlatformPaymentMethods = useCallback(async () => {
    setLoading(true);
    try {
      const methods = await fetchPlatformPaymentMethodList();
      setRows(methods);
      if (methods.length > 0) {
        setHasAnyPaymentAccounts(true);
      }
    } catch (error) {
      console.error("Error loading platform payment accounts:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlatformPaymentMethods();
  }, [fetchPlatformPaymentMethods]);

  const isAtLimit = rows.length >= PLATFORM_PAYMENT_METHOD_LIMIT;

  // PaymentAccountCardItem drives its own infinite-scroll off this shape; the platform list is small and
  // single-page, so hasMore is always false and fetchData just re-reads the full list.
  const dataHookState = {
    rows,
    loading,
    hasMore: false,
    fetchData: () => fetchPlatformPaymentMethods(),
    currentPage: 1,
  };

  // Memoized so AddPlaidAccount's onPlaidSuccess effect (which lists onPlaidSuccess in its deps) is not re-fired
  // by a new callback identity on every render — a stable identity is part of preventing the post-add fetch loop.
  const handlePlaidSuccess = useCallback((_plaidData: PlaidAccountDetails) => {
    setAddModalOpen(false);
    setReconnectModalOpen(false);
    setReconnectPaymentMethod(null);
    fetchPlatformPaymentMethods();
  }, [fetchPlatformPaymentMethods]);

  const handleReconnectPlaid = useCallback((paymentMethod: PaymentMethodVM) => {
    setReconnectPaymentMethod(paymentMethod);
    setReconnectModalOpen(true);
  }, []);

  const handleRenamePlaidAccount = useCallback((paymentMethod: PaymentMethodVM) => {
    setRenamePaymentMethod(paymentMethod);
    setRenameModalOpen(true);
  }, []);

  return (
    <div className="payment-accounts-page content-wrapper container-fluid">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h2 className="mb-0">Payment Accounts</h2>
          <p className="text-muted mb-0">
            Connect the Plaid payment gateway used to collect subscription fees from organizations.
          </p>
        </div>
        {/* The platform gateway is a single account (cap = 1); once connected it is managed via the card's
            Reconnect/Delete actions, so hide the Add button at the limit rather than leaving a dead disabled one.
            Deleting the account (rows → 0) brings the Add path back. */}
        {hasAnyPaymentAccounts && !isAtLimit && (
          <SimpleButton
            label="Add Payment Account"
            onClick={() => setAddModalOpen(true)}
          />
        )}
      </div>

      <PaymentAccountCardItem
        dataHookState={dataHookState}
        onReconnectPlaid={handleReconnectPlaid}
        onRenamePlaidAccount={handleRenamePlaidAccount}
        // PaymentAccountCardItem's empty-state "Add" button calls onEditPaymentMethod; there is no legacy
        // ACH/CreditCard edit flow for the Plaid-only platform gateway (showEditAction=false), so reuse this
        // hook only to open the Add modal. Renaming the connected Plaid account uses onRenamePlaidAccount above.
        onEditPaymentMethod={isAtLimit ? undefined : () => setAddModalOpen(true)}
        className="organisation-listing-card"
        hasAnyPaymentAccounts={hasAnyPaymentAccounts}
        showEditAction={false}
        alwaysAllowActions={true}
      />

      <PlaidPaymentAccountModals
        isAddModalOpen={isAddModalOpen}
        onAddModalClose={() => setAddModalOpen(false)}
        isReconnectModalOpen={isReconnectModalOpen}
        reconnectPaymentMethod={reconnectPaymentMethod}
        onReconnectModalClose={() => {
          setReconnectModalOpen(false);
          setReconnectPaymentMethod(null);
        }}
        onPlaidSuccess={handlePlaidSuccess}
        // The backend derives the platform entity + payee from the Platform-role JWT; this value only has to
        // satisfy the VM's "EntityTypeId != 0" guard, so send the Platform entity type (not All=0).
        plaidEntityConfig={{ entityTypeId: PLATFORM_PM_ENTITY_TYPE_ID, entityId: 0 }}
      />

      <EditPlaidAccountNameModal
        isOpen={isRenameModalOpen}
        paymentMethod={renamePaymentMethod}
        onClose={() => {
          setRenameModalOpen(false);
          setRenamePaymentMethod(null);
        }}
        onRenamed={() => {
          setRenameModalOpen(false);
          setRenamePaymentMethod(null);
          fetchPlatformPaymentMethods();
        }}
      />
    </div>
  );
};

export default PlatformPaymentAccountsPage;
